import { Link } from "react-router-dom";
import { Sparkles, ClipboardCheck, RefreshCw, AlertOctagon, Dumbbell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Kpi, Section, Empty, StatusBadge } from "@/components/kit";
import useLoad from "@/lib/useLoad";
import { fmtDate, money } from "@/lib/api";

function Row({ to, title, sub, right, testid }) {
  return (
    <Link to={to} className="row-hover flex items-center justify-between gap-3 px-3 py-2.5 rounded-md" data-testid={testid}>
      <div className="min-w-0"><div className="text-sm font-medium truncate">{title}</div><div className="text-xs text-slate-500 truncate">{sub}</div></div>
      {right}
    </Link>
  );
}

export default function Dashboard() {
  const [d] = useLoad("/coach/dashboard");
  if (!d || Array.isArray(d) || !d.counts || !d.revenue_month) {
    return <div className="eyebrow">Loading command center…</div>;
  }
  const c = d.counts;
  const rev = Object.entries(d.revenue_month);
  return (
    <div>
      <PageHeader eyebrow="Coach OS · Command center" title="Today's operations" testid="dashboard-title">
        <Button asChild variant="outline"><Link to="/coach/clients" data-testid="dash-clients-btn">Clients</Link></Button>
        <Button asChild><Link to="/coach/ai" data-testid="dash-ai-draft-btn"><Sparkles size={15} className="mr-1" />AI draft</Link></Button>
      </PageHeader>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4 mb-8">
        <Kpi label="Active clients" value={c.active_clients} color="#10B981" to="/coach/clients" testid="kpi-active-clients" />
        <Kpi label="Expired members" value={c.expired} color="#EF4444" to="/coach/clients?status=expired" testid="kpi-expired" />
        <Kpi label="Check-ins to review" value={c.pending_checkins} color="#FF3B30" testid="kpi-pending-checkins" />
        <Kpi label="AI drafts" value={c.ai_drafts} color="#F59E0B" to="/coach/library" testid="kpi-ai-drafts" />
        <Kpi label="Renewals ≤7d" value={c.renewals_due} color="#06B6D4" to="/coach/billing" testid="kpi-renewals" />
        <Kpi label="Failed payments" value={c.failed_payments} color="#EF4444" to="/coach/billing" testid="kpi-failed-payments" />
        <Kpi label="Revenue (month)" value={rev.length ? money(rev[0][1], rev[0][0]) : "-"} color="#10B981" testid="kpi-revenue"
          sub={rev.slice(1).map(([k, v]) => money(v, k)).join(" · ")} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Section title="Check-ins awaiting review" testid="dash-pending-checkins" action={<ClipboardCheck size={18} className="text-[#FF3B30]" />}>
          {d.pending_checkins.length ? d.pending_checkins.map((ck) => (
            <Row key={ck.id} to={`/coach/clients/${ck.client_id}?tab=checkins`} title={ck.client_name} sub={`Submitted ${fmtDate(ck.submitted_at)}`}
              right={<StatusBadge status="pending" />} testid={`dash-checkin-${ck.id}`} />
          )) : <Empty text="All check-ins reviewed." />}
        </Section>
        <Section title="AI drafts to approve" testid="dash-ai-drafts" action={<Sparkles size={18} className="text-amber-400" />}>
          {d.ai_drafts.length ? d.ai_drafts.map((p) => (
            <Row key={p.id} to={`/coach/plans/${p.id}`} title={p.title} sub={`${p.type} · ${p.client_name || "template"}`}
              right={<StatusBadge status="ai_draft" />} testid={`dash-draft-${p.id}`} />
          )) : <Empty text="No drafts pending." />}
        </Section>
        <Section title="Renewals due & expired" testid="dash-renewals" action={<RefreshCw size={18} className="text-cyan-400" />}>
          {d.renewals.length + d.expired.length ? [...d.renewals, ...d.expired].map((m) => (
            <Row key={m.id} to={`/coach/clients/${m.id}?tab=billing`} title={m.name} sub={`${m.package_name || "-"} · ${m.status === "expired" ? "ended" : "ends"} ${fmtDate(m.end_date)}`}
              right={<StatusBadge status={m.status} />} testid={`dash-renewal-${m.id}`} />
          )) : <Empty text="No renewals due this week." />}
        </Section>
        <Section title="Failed payments" testid="dash-failed" action={<AlertOctagon size={18} className="text-red-400" />}>
          {d.failed_invoices.length ? d.failed_invoices.map((i) => (
            <Row key={i.id} to="/coach/billing" title={`${i.client_name} · ${money(i.total, i.currency)}`} sub={`${i.number} · ${i.failure_reason || "failed"} · ${i.attempts} attempt(s)`}
              right={<StatusBadge status="failed" />} testid={`dash-failed-${i.id}`} />
          )) : <Empty text="No failed payments." />}
        </Section>
        <Section title="Recent workout logs" className="xl:col-span-2" testid="dash-recent-logs" action={<Dumbbell size={18} className="text-emerald-400" />}>
          {d.recent_logs.length ? d.recent_logs.map((l) => (
            <Row key={l.id} to={`/coach/clients/${l.client_id}?tab=logs`} title={`${l.client_name} · ${l.day_name || "Workout"}`}
              sub={`${l.date} · ${l.entries.length} exercises${l.rpe ? ` · RPE ${l.rpe}` : ""}${l.edited ? " · edited" : ""}`} testid={`dash-log-${l.id}`} />
          )) : <Empty text="No logs yet." />}
        </Section>
      </div>
    </div>
  );
}
